import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, ExternalLink, ClipboardCopy, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MarkdownText } from '@/components/ui/markdown-text'
import { cn } from '@/lib/utils'

interface SetupInstructionsProps {
  integrationType: string
  isConnected: boolean
  projectUrl?: string
  className?: string
  deepLinks?: { label: string; url: string }[]
  copyableUrls?: { label: string; value: string }[]
}

export function SetupInstructions({
  integrationType,
  isConnected,
  projectUrl,
  className,
  deepLinks,
  copyableUrls
}: SetupInstructionsProps) {
  const { t } = useTranslation('conversations')
  const [isVisible, setIsVisible] = useState(!isConnected)
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)

  // Get steps from translations with projectUrl interpolation
  const steps = t(
    `integrations.setupInstructions.${integrationType}.steps`,
    {
      returnObjects: true,
      defaultValue: null,
      projectUrl: projectUrl || 'your-app-url'
    }
  )

  // Get deep links from translations (fallback to prop)
  const translatedDeepLinks = t(
    `integrations.setupInstructions.${integrationType}.deepLinks`,
    { returnObjects: true, defaultValue: null }
  )
  const resolvedDeepLinks = (Array.isArray(translatedDeepLinks) ? translatedDeepLinks : deepLinks) || []

  const handleCopyUrl = (value: string) => {
    navigator.clipboard.writeText(value)
    setCopiedUrl(value)
    setTimeout(() => setCopiedUrl(null), 2000)
  }

  // If no instructions and no deep links exist, don't render anything
  const hasSteps = steps && Array.isArray(steps) && steps.length > 0
  const hasDeepLinks = resolvedDeepLinks.length > 0
  const hasCopyableUrls = copyableUrls && copyableUrls.length > 0

  if (!hasSteps && !hasDeepLinks && !hasCopyableUrls) {
    return null
  }

  return (
    <div className={cn("space-y-3", className)}>
      {/* Header with toggle if connected */}
      {isConnected && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsVisible(!isVisible)}
          className="flex items-center gap-2 p-0 h-auto hover:bg-transparent"
        >
          {isVisible ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <span className="text-sm font-medium">
            {isVisible
              ? t('integrations.setupInstructions.hideInstructions')
              : t('integrations.setupInstructions.viewInstructions')
            }
          </span>
        </Button>
      )}

      {/* Instructions list */}
      {isVisible && (
        <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-4">
          {/* Deep link buttons */}
          {hasDeepLinks && (
            <div className="flex flex-wrap gap-2">
              {resolvedDeepLinks.map((link: { label: string; url: string }, index: number) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-md transition-colors"
                >
                  <ExternalLink className="h-3 w-3" />
                  {link.label}
                </a>
              ))}
            </div>
          )}

          {/* Copyable URLs section */}
          {hasCopyableUrls && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                {t('integrations.setupInstructions.urlsToPaste', { defaultValue: 'URLs to paste into your provider dashboard:' })}
              </p>
              {copyableUrls!.map((urlItem, index) => (
                <div key={index} className="flex items-center gap-2 bg-card rounded-md border p-2">
                  <span className="text-xs text-muted-foreground flex-shrink-0">{urlItem.label}:</span>
                  <code className="text-xs font-mono text-foreground flex-1 min-w-0 truncate">{urlItem.value}</code>
                  <button
                    type="button"
                    onClick={() => handleCopyUrl(urlItem.value)}
                    className="flex-shrink-0 p-1 text-muted-foreground hover:text-foreground transition-colors rounded hover:bg-accent"
                  >
                    {copiedUrl === urlItem.value ? (
                      <Check className="h-3.5 w-3.5 text-green-500" />
                    ) : (
                      <ClipboardCopy className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Steps */}
          {hasSteps && (
            <>
              <h4 className="text-sm font-semibold text-foreground">
                {t('integrations.setupInstructions.title')}
              </h4>
              <ol className="space-y-3">
                {steps.map((step: string | object, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                      {index + 1}
                    </span>
                    <MarkdownText className="text-sm text-foreground/90 flex-1 min-w-0 leading-relaxed" children={String(step)} />
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </div>
  )
}
